import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, DollarSign, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (amount, currency = 'INR') => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency === 'INR' ? 'INR' : 'USD',
    maximumFractionDigits: 2
  }).format(amount).replace('$', currency + ' ');
};

export default function ForexPaymentsPage() {
  const { getAuthHeader } = useAuth();
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewPayment, setViewPayment] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/forex-payments?${params}`, {
        headers: getAuthHeader()
      });
      setPayments(response.data.payments || []);
    } catch (error) {
      toast.error('Failed to fetch forex payments');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, statusFilter]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/suppliers`, {
        headers: getAuthHeader()
      });
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch suppliers');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchPayments();
    fetchSuppliers();
  }, [fetchPayments, fetchSuppliers]);

  const getStatusBadge = (payment) => {
    const status = payment.status;
    const colors = {
      open: 'bg-yellow-100 text-yellow-800',
      boe_linked: 'bg-blue-100 text-blue-800',
      closed: 'bg-green-100 text-green-800'
    };
    const labels = {
      open: 'Open',
      boe_linked: 'BOE Linked',
      closed: 'Closed'
    };
    return (
      <Badge className={colors[status] || 'bg-gray-100'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getMatchBadge = (payment) => {
    if (!payment.import_shipment_id) return null;
    if (payment.is_amount_matched) {
      return (
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" /> Matched
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-red-100 text-red-800">
          <AlertCircle className="h-3 w-3 mr-1" /> Diff: {formatCurrency(payment.amount_difference, payment.forex_currency)}
        </Badge>
      );
    }
  };

  // Summary
  const summary = {
    total: payments.length,
    open: payments.filter(p => p.status === 'open').length,
    linked: payments.filter(p => p.status === 'boe_linked').length,
    closed: payments.filter(p => p.status === 'closed').length,
    totalInr: payments.reduce((sum, p) => sum + (p.inr_amount || 0), 0)
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="forex-payments-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Forex Payments</h1>
            <p className="text-gray-500 text-sm mt-1">
              Track international payments and BOE reconciliation
            </p>
          </div>
          {/* Removed standalone "New Forex Payment" button - forex records can only be created from bank transactions */}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total Payments</div>
              <div className="text-2xl font-bold">{summary.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Open</div>
              <div className="text-2xl font-bold text-yellow-600">{summary.open}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">BOE Linked</div>
              <div className="text-2xl font-bold text-blue-600">{summary.linked}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Closed</div>
              <div className="text-2xl font-bold text-green-600">{summary.closed}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total INR</div>
              <div className="text-xl font-bold">{formatCurrency(summary.totalInr)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Payments List */}
        <div className="mt-4">
          {/* Filter */}
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="flex gap-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="boe_linked">BOE Linked</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Payments Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>FX Number</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Forex Amount</TableHead>
                    <TableHead>INR Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Match</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
                    </TableRow>
                  ) : payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No forex payments found. Create a bank transaction with the "Forex Payment" toggle enabled.
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map(payment => (
                      <TableRow key={payment.id}>
                        <TableCell className="font-medium">{payment.forex_payment_number}</TableCell>
                        <TableCell>{payment.payment_date}</TableCell>
                        <TableCell>
                          <div>{payment.supplier_name}</div>
                          {payment.supplier_pi_number && (
                            <div className="text-xs text-gray-500">PI: {payment.supplier_pi_number}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {payment.forex_currency} {payment.forex_amount?.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">@ {payment.exchange_rate}</div>
                        </TableCell>
                        <TableCell>{formatCurrency(payment.inr_amount)}</TableCell>
                        <TableCell>{getStatusBadge(payment)}</TableCell>
                        <TableCell>{getMatchBadge(payment)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewPayment(payment)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* View Dialog */}
        <Dialog open={!!viewPayment} onOpenChange={() => setViewPayment(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Forex Payment Details</DialogTitle>
            </DialogHeader>
            {viewPayment && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Payment Number</Label>
                    <div className="font-medium">{viewPayment.forex_payment_number}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Status</Label>
                    <div>{getStatusBadge(viewPayment)}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Supplier</Label>
                    <div className="font-medium">{viewPayment.supplier_name}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">PI Number</Label>
                    <div>{viewPayment.supplier_pi_number || '-'}</div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Forex Details</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-500">Forex Amount</Label>
                      <div className="font-medium">
                        {viewPayment.forex_currency} {viewPayment.forex_amount?.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-500">Exchange Rate</Label>
                      <div>{viewPayment.exchange_rate}</div>
                    </div>
                    <div>
                      <Label className="text-gray-500">INR Amount</Label>
                      <div className="font-medium">{formatCurrency(viewPayment.inr_amount)}</div>
                    </div>
                  </div>
                </div>

                {viewPayment.import_shipment_id && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">BOE Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-500">BOE Number</Label>
                        <div>{viewPayment.boe_number || '-'}</div>
                      </div>
                      <div>
                        <Label className="text-gray-500">Amount Match</Label>
                        <div>{getMatchBadge(viewPayment)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
